<!-- "Test your microphone & camera": a live preview of what colleagues will
     see and hear, device pickers (camera / microphone / speakers), a speaker
     test chime and the desktop-notification opt-in. Opened from the call panel,
     the call window (gear) and the What's new popup; the choices are saved per
     browser and applied live to a call in progress (meeting.applyDevices). -->
<template>
  <vs-popup :active.sync="open" title="Test your microphone & camera" class="device-check-popup">
    <div class="dc">
      <p class="dc__intro">This is what your colleagues will see and hear. Your choices are kept for your next calls.</p>

      <div class="dc__grid">
        <!-- Camera -->
        <section class="dc__card">
          <header class="dc__head">
            <feather-icon icon="VideoIcon" svgClasses="w-4 h-4" />
            <span>Camera</span>
            <span class="dc__state" :class="camOk ? 'is-ok' : 'is-ko'">{{ camOk ? 'Working' : (camError || 'No camera found') }}</span>
          </header>
          <div class="dc__preview">
            <video v-show="camOk" ref="video" autoplay playsinline muted class="dc__video"></video>
            <div v-if="!camOk" class="dc__preview-empty">
              <feather-icon icon="VideoOffIcon" svgClasses="w-8 h-8" />
              <span>{{ camError || 'No camera found' }}</span>
              <small>You can still make audio calls.</small>
            </div>
          </div>
          <select v-model="videoIn" class="dc__select" :disabled="!devices.videoIn.length">
            <option value="">Default camera</option>
            <option v-for="d in devices.videoIn" :key="d.deviceId" :value="d.deviceId">{{ d.label }}</option>
          </select>
        </section>

        <!-- Microphone -->
        <section class="dc__card">
          <header class="dc__head">
            <feather-icon icon="MicIcon" svgClasses="w-4 h-4" />
            <span>Microphone</span>
            <span class="dc__state" :class="micOk ? (heard ? 'is-ok' : 'is-wait') : 'is-ko'">
              {{ micOk ? (heard ? 'We hear you' : 'Say something…') : (micError || 'No microphone') }}
            </span>
          </header>
          <div class="dc__meter" :class="{ 'is-off': !micOk }">
            <div class="dc__meter-fill" :class="{ 'is-hot': level > 75 }" :style="{ width: `${level}%` }"></div>
            <div class="dc__meter-ticks"><span v-for="n in 12" :key="n"></span></div>
          </div>
          <p class="dc__hint">Speak normally: the bar should move with your voice.</p>
          <select v-model="audioIn" class="dc__select" :disabled="!devices.audioIn.length">
            <option value="">Default microphone</option>
            <option v-for="d in devices.audioIn" :key="d.deviceId" :value="d.deviceId">{{ d.label }}</option>
          </select>
        </section>

        <!-- Speakers -->
        <section class="dc__card">
          <header class="dc__head">
            <feather-icon icon="Volume2Icon" svgClasses="w-4 h-4" />
            <span>Speakers</span>
            <span v-if="chimePlayed" class="dc__state is-wait">Did you hear the chime?</span>
          </header>
          <select v-if="devices.audioOut.length" v-model="audioOut" class="dc__select">
            <option value="">Default speakers</option>
            <option v-for="d in devices.audioOut" :key="d.deviceId" :value="d.deviceId">{{ d.label }}</option>
          </select>
          <p v-else class="dc__hint">Your browser plays calls on the system default speakers.</p>
          <vs-button type="border" size="small" icon-pack="feather" icon="icon-play" @click="playChime">Play a test sound</vs-button>
        </section>

        <!-- Notifications -->
        <section class="dc__card">
          <header class="dc__head">
            <feather-icon icon="BellIcon" svgClasses="w-4 h-4" />
            <span>Incoming calls</span>
            <span class="dc__state" :class="notifClass">{{ notifLabel }}</span>
          </header>
          <p class="dc__hint">Get a desktop notification when someone calls you while this tab is in the background.</p>
          <vs-button v-if="notifState === 'default'" type="border" size="small" icon-pack="feather" icon="icon-bell" @click="askNotifications">Enable notifications</vs-button>
          <p v-else-if="notifState === 'denied'" class="dc__hint dc__hint--warn">Blocked by the browser: allow notifications for this site from the address bar.</p>
        </section>
      </div>

      <footer class="dc__footer">
        <span class="dc__summary" :class="allGood ? 'is-ok' : 'is-wait'">
          <feather-icon :icon="allGood ? 'CheckCircleIcon' : 'AlertCircleIcon'" svgClasses="w-4 h-4" />
          <span>{{ summary }}</span>
        </span>
        <div class="dc__actions">
          <vs-button type="border" @click="open = false">Close</vs-button>
          <vs-button icon-pack="feather" icon="icon-check" @click="save">Use these devices</vs-button>
        </div>
      </footer>
    </div>
  </vs-popup>
</template>

<script>
import meeting from '@/services/meeting.js'
import { getLocalMedia, listDevices, stopStream } from '@/services/webrtc.js'
import { playTestChime } from '@/services/ringtone.js'

const EMPTY = () => ({ audioIn: [], videoIn: [], audioOut: [] })

function describe (e) {
  const name = e && e.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Blocked by the browser'
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'Not found'
  if (name === 'NotReadableError' || name === 'TrackStartError') return 'Used by another app'
  return 'Unavailable'
}

export default {
  data () {
    return {
      devices: EMPTY(),
      audioIn: '',
      videoIn: '',
      audioOut: '',
      level: 0,
      heard: false,
      camOk: false,
      camError: '',
      micOk: false,
      micError: '',
      chimePlayed: false,
      notifState: 'unsupported',
      ready: false
    }
  },
  computed: {
    open: {
      get () { return meeting.state.ui.deviceCheck },
      set (v) { meeting.state.ui.deviceCheck = v }
    },
    allGood () { return this.micOk && this.heard },
    summary () {
      if (!this.micOk) return 'Without a microphone your colleagues cannot hear you.'
      if (!this.heard) return 'Say a few words to check the microphone.'
      return this.camOk ? 'Microphone and camera are ready.' : 'Microphone ready — audio-only calls.'
    },
    notifLabel () {
      if (this.notifState === 'granted') return 'Enabled'
      if (this.notifState === 'denied') return 'Blocked'
      if (this.notifState === 'default') return 'Off'
      return 'Not supported'
    },
    notifClass () {
      if (this.notifState === 'granted') return 'is-ok'
      if (this.notifState === 'denied') return 'is-ko'
      return 'is-wait'
    }
  },
  watch: {
    open (val) { if (val) this.start(); else this.stop() },
    audioIn () { if (this.ready) this.acquire() },
    videoIn () { if (this.ready) this.acquire() }
  },
  created () {
    // Non-reactive media handles (Vue 2 must not observe them).
    this.stream = null
    this.owned = false
    this.audioCtx = null
    this.raf = null
  },
  beforeDestroy () { this.stop() },
  methods: {
    async start () {
      const prefs = meeting.state.devices || {}
      this.ready = false
      this.audioIn = prefs.audioIn || ''
      this.videoIn = prefs.videoIn || ''
      this.audioOut = prefs.audioOut || ''
      this.heard = false
      this.chimePlayed = false
      this.notifState = ('Notification' in window) ? Notification.permission : 'unsupported'
      await this.acquire()
      try { this.devices = await listDevices() } catch (e) { this.devices = EMPTY() }
      this.ready = true
    },
    async acquire () {
      this.release()
      let stream = null
      try {
        stream = await getLocalMedia({ audioIn: this.audioIn, videoIn: this.videoIn })
      } catch (e) {
        // Some webcams refuse a second opening while a call uses them: show
        // the call's own stream instead of an error.
        const live = meeting.getLocalStream()
        if (live) { this.attach(live, false); return }
        this.micOk = false
        this.camOk = false
        this.micError = describe(e)
        this.camError = describe(e)
        return
      }
      this.attach(stream, true)
      // Labels only show up once a permission has been granted: refresh the lists.
      if (this.ready) { try { this.devices = await listDevices() } catch (e) { /* keep */ } }
    },
    attach (stream, owned) {
      this.stream = stream
      this.owned = owned
      const v = this.stream.getVideoTracks()[0]
      const a = this.stream.getAudioTracks()[0]
      this.camOk = !!v
      this.camError = v ? '' : 'No camera found'
      this.micOk = !!a
      this.micError = a ? '' : 'No microphone'
      this.$nextTick(() => {
        const el = this.$refs.video
        if (el) {
          el.srcObject = this.stream
          const p = el.play()
          if (p && p.catch) p.catch(() => {})
        }
      })
      if (a) this.startMeter()
    },
    startMeter () {
      const AC = window.AudioContext || window.webkitAudioContext
      if (!AC) return
      try {
        this.audioCtx = new AC()
        const source = this.audioCtx.createMediaStreamSource(this.stream)
        const analyser = this.audioCtx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)
        const data = new Uint8Array(analyser.fftSize)
        const loop = () => {
          analyser.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) {
            const d = (data[i] - 128) / 128
            sum += d * d
          }
          const rms = Math.sqrt(sum / data.length)
          const instant = Math.min(100, Math.round(rms * 320))
          this.level = Math.max(instant, Math.round(this.level * 0.85)) // quick attack, slow release
          if (instant > 10) this.heard = true
          this.raf = requestAnimationFrame(loop)
        }
        loop()
      } catch (e) { /* meter is best-effort */ }
    },
    release () {
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null }
      if (this.audioCtx) {
        try { this.audioCtx.close() } catch (e) { /* noop */ }
        this.audioCtx = null
      }
      if (this.owned) stopStream(this.stream) // never stop the call's own tracks
      this.stream = null
      const el = this.$refs.video
      if (el) el.srcObject = null
      this.level = 0
    },
    stop () {
      this.release()
      this.ready = false
    },
    async playChime () {
      this.chimePlayed = true
      await playTestChime(this.audioOut)
    },
    askNotifications () {
      if (!('Notification' in window)) return
      Notification.requestPermission().then((p) => { this.notifState = p })
    },
    save () {
      meeting.applyDevices({ audioIn: this.audioIn, videoIn: this.videoIn, audioOut: this.audioOut })
      this.open = false
      this.$vs.notify({ title: 'Devices saved', text: 'Your microphone, camera and speakers are set for your calls.', color: 'success', position: 'top-right', time: 3000 })
    }
  }
}
</script>

<style>
.device-check-popup .vs-popup { width: 820px; max-width: calc(100vw - 24px); border-radius: 16px; }
.device-check-popup .vs-popup--content { padding: 0 22px 22px; max-height: calc(100vh - 120px); overflow-x: hidden; }
</style>

<style scoped>
.dc { min-width: 0; }
.dc__intro { margin: 2px 0 16px; font-size: 13px; color: #64748b; }
.dc__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.dc__card {
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px; border-radius: 14px; background: #f8fafc; border: 1px solid #e9edf3;
}
.dc__head { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 13px; color: #1e293b; }
.dc__state {
  margin-left: auto; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 999px;
  background: #e2e8f0; color: #475569;
}
.dc__state.is-ok { background: rgba(40,199,111,.14); color: #1f9d57; }
.dc__state.is-ko { background: rgba(234,84,85,.14); color: #d63031; }
.dc__state.is-wait { background: rgba(255,159,67,.16); color: #c76f1e; }

.dc__preview {
  position: relative; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden;
  background: #0f0f1e; display: flex; align-items: center; justify-content: center;
}
.dc__video { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); display: block; }
.dc__preview-empty {
  display: flex; flex-direction: column; align-items: center; gap: 6px; color: rgba(255,255,255,.7); font-size: 13px; text-align: center; padding: 10px;
}
.dc__preview-empty small { color: rgba(255,255,255,.45); font-size: 11px; }

.dc__meter { position: relative; height: 18px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
.dc__meter.is-off { opacity: .4; }
.dc__meter-fill {
  position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px;
  background: linear-gradient(90deg, #28c76f, #7ddf9e);
  transition: width .06s linear;
}
.dc__meter-fill.is-hot { background: linear-gradient(90deg, #28c76f, #ff9f43); }
.dc__meter-ticks { position: absolute; inset: 0; display: flex; }
.dc__meter-ticks span { flex: 1; border-right: 2px solid #f8fafc; }
.dc__meter-ticks span:last-child { border-right: none; }

.dc__hint { margin: 0; font-size: 12px; color: #64748b; }
.dc__hint--warn { color: #c76f1e; }
.dc__select {
  width: 100%; padding: 8px 10px; border-radius: 8px; border: 1px solid #dfe5ee; background: #fff;
  font-size: 13px; color: #1e293b;
}
.dc__select:disabled { opacity: .6; }

.dc__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 16px; flex-wrap: wrap; }
.dc__summary { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
.dc__summary.is-ok { color: #1f9d57; }
.dc__summary.is-wait { color: #c76f1e; }
.dc__actions { display: flex; gap: 10px; margin-left: auto; }
@media (max-width: 680px) { .dc__grid { grid-template-columns: 1fr; } }
</style>
